import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { fetchFaq, type FaqEntry } from '@/lib/api'
import { Card } from '@/components/Surface'
import { SubPage } from '@/components/SubPage'
import { Segment } from '@/components/Form'
import { motion, AnimatePresence } from 'motion/react'
import { haptic } from '@/lib/tg'

const CATEGORIES = [
  { value: 'all' as const, label: 'все' },
  { value: 'install' as const, label: 'установка' },
  { value: 'pay' as const, label: 'оплата' },
  { value: 'device' as const, label: 'устройства' },
]

type Cat = (typeof CATEGORIES)[number]['value']

export function Faq() {
  const { data } = useQuery({ queryKey: ['faq'], queryFn: fetchFaq })
  const [cat, setCat] = useState<Cat>('all')
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState<string | null>(null)

  const items = useMemo(() => {
    let list = data ?? []
    if (cat !== 'all') list = list.filter(e => e.category === cat)
    const q = query.trim().toLowerCase()
    if (q) list = list.filter(e => e.q.toLowerCase().includes(q) || e.a.toLowerCase().includes(q))
    return list
  }, [data, cat, query])

  return (
    <SubPage title="база знаний" subtitle="инструкции и типовые вопросы">
      <div className="px-5 space-y-3 stagger">
        <Card className="p-1" style={{ ['--i' as never]: 0 }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="поиск"
            className="w-full bg-transparent px-4 py-3 text-[15px] placeholder:text-[var(--color-dim)] outline-none"
          />
        </Card>

        <div style={{ ['--i' as never]: 1 }}>
          <Segment value={cat} onChange={setCat} options={CATEGORIES} />
        </div>

        <Card className="overflow-hidden" style={{ ['--i' as never]: 2 }}>
          {items.map((entry, i, arr) => (
            <FaqRow
              key={entry.id}
              entry={entry}
              divider={i < arr.length - 1}
              isOpen={open === entry.id}
              onToggle={() => {
                haptic('light')
                setOpen(open === entry.id ? null : entry.id)
              }}
            />
          ))}
          {items.length === 0 && (
            <div className="px-5 py-10 text-center text-[13px] text-[var(--color-muted)]">
              ничего не нашлось
            </div>
          )}
        </Card>
      </div>
    </SubPage>
  )
}

function FaqRow({
  entry,
  divider,
  isOpen,
  onToggle,
}: {
  entry: FaqEntry
  divider: boolean
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <div style={{ borderBottom: divider ? '1px solid var(--color-line)' : 'none' }}>
      <button
        onClick={onToggle}
        className="tap w-full text-left px-5 py-4 flex items-center gap-3 active:bg-[var(--color-surface)]"
      >
        <div className="flex-1 text-[14.5px] font-semibold">{entry.q}</div>
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--color-dim)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </motion.svg>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 text-[13.5px] leading-relaxed text-[var(--color-muted)]">
              {entry.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
